import React from 'react';
import type { AccountRisk } from './RiskScorePill';

export type ManagedAccountsListRow = {
  id: string;
  title: string;
  typeLabel?: string;
  risk?: AccountRisk | null;
  balance: string;
  logoSrc?: string;
  /** Where the row leads (the account page). */
  href: string;
};

export type ManagedAccountsListProps = {
  title?: string;
  total: string;
  rows: ManagedAccountsListRow[];
  /**
   * Called on a plain click so the app can route without a page load; clicks
   * with a modifier key keep the browser's default (new tab, etc.).
   */
  onOpen?: (row: ManagedAccountsListRow) => void;
};

const isPlainClick = (event: React.MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Compact list of managed accounts for the wealth summary column (rework
 * board Main): name, type and risk, balance; each row opens the account.
 */
const ManagedAccountsList: React.FC<ManagedAccountsListProps> = ({
  title = 'Managed accounts',
  total,
  rows,
  onOpen,
}) => {
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="managed-accounts-list" aria-label={title}>
      <header>
        <h2>{title}</h2>
        <span className="managed-accounts-total">{total}</span>
      </header>
      <ul>
        {rows.map((row) => {
          const subtitle = [row.typeLabel, row.risk?.level].filter(Boolean).join(' · ');
          return (
            <li key={row.id}>
              <a
                href={row.href}
                onClick={(event) => {
                  if (onOpen && isPlainClick(event)) {
                    event.preventDefault();
                    onOpen(row);
                  }
                }}
              >
                {row.logoSrc ? (
                  <img className="managed-accounts-mark" src={row.logoSrc} alt="" />
                ) : null}
                <span className="managed-accounts-copy">
                  <strong>{row.title}</strong>
                  {subtitle ? <small>{subtitle}</small> : null}
                </span>
                <span className="managed-accounts-balance">{row.balance}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ManagedAccountsList;
